import { k8sRequest, makeK8sCreator } from "../api";
import { formatAge } from "../format";
import { decodedByteLength, encodeSecretValue } from "./encoding";
import {
    ConfigMapDetail,
    ConfigMapRow,
    K8sConfigMap,
    K8sConfigMapList,
    K8sSecret,
    K8sSecretList,
    SecretRow,
} from "./types";

/** Namespace-scoped, and the section works in default throughout. */
const NAMESPACE = "default";

const keyList = (data?: { [key: string]: string }): string[] => Object.keys(data || {}).sort();

const toConfigMapRow = (configMap: K8sConfigMap): ConfigMapRow => {
    const keys = keyList(configMap.data);

    return {
        id: `${configMap.metadata.namespace || NAMESPACE}/${configMap.metadata.name}`,
        name: configMap.metadata.name,
        namespace: configMap.metadata.namespace || NAMESPACE,
        keyCount: keys.length,
        keys: keys.join(", "),
        age: formatAge(configMap.metadata.creationTimestamp),
    };
};

export const parseConfigMapList = (body: unknown): ConfigMapRow[] =>
    (((body || {}) as K8sConfigMapList).items || []).map(toConfigMapRow);

export const parseConfigMap = (body: unknown): ConfigMapDetail | undefined => {
    const configMap = body as K8sConfigMap | undefined;

    if (!configMap || !configMap.metadata || !configMap.metadata.name) {
        return undefined;
    }

    const data = configMap.data || {};

    return {
        name: configMap.metadata.name,
        namespace: configMap.metadata.namespace || NAMESPACE,
        age: formatAge(configMap.metadata.creationTimestamp),
        created: configMap.metadata.creationTimestamp || "",
        entries: keyList(data).map((key) => ({ key, value: data[key] })),
    };
};

/**
 * Secrets are flattened to keys and sizes only. There is no value on the row
 * and no detail parser that could produce one, so no later change to a view
 * can accidentally render a credential.
 */
const toSecretRow = (secret: K8sSecret): SecretRow => {
    const data = secret.data || {};
    const keys = keyList(data);

    return {
        id: `${secret.metadata.namespace || NAMESPACE}/${secret.metadata.name}`,
        name: secret.metadata.name,
        namespace: secret.metadata.namespace || NAMESPACE,
        type: secret.type || "",
        keyCount: keys.length,
        keys: keys.join(", "),
        sizes: keys.map((key) => `${key}: ${decodedByteLength(data[key])} B`).join(", "),
        age: formatAge(secret.metadata.creationTimestamp),
    };
};

export const parseSecretList = (body: unknown): SecretRow[] =>
    (((body || {}) as K8sSecretList).items || []).map(toSecretRow);

/** Build the ConfigMap body for a create request. */
export const buildConfigMap = (name: string, entries: { key: string; value: string }[]): K8sConfigMap =>
    ({
        apiVersion: "v1",
        kind: "ConfigMap",
        metadata: { name, namespace: NAMESPACE },
        data: entries.reduce((data, entry) => ({ ...data, [entry.key]: entry.value }), {}),
    } as K8sConfigMap);

/** Build the Secret body for a create request, base64-encoding each value. */
export const buildSecret = (name: string, entries: { key: string; value: string }[]): K8sSecret =>
    ({
        apiVersion: "v1",
        kind: "Secret",
        type: "Opaque",
        metadata: { name, namespace: NAMESPACE },
        data: entries.reduce((data, entry) => ({ ...data, [entry.key]: encodeSecretValue(entry.value) }), {}),
    } as K8sSecret);

const configMaps = makeK8sCreator<ConfigMapRow[]>("k8sConfigMaps", parseConfigMapList);
const configMap = makeK8sCreator<ConfigMapDetail | undefined>("k8sConfigMap", parseConfigMap);
const secrets = makeK8sCreator<SecretRow[]>("k8sSecrets", parseSecretList);
const created = makeK8sCreator<unknown>("k8sConfigCreate", (body) => body);

const base = (resource: string) => `api/v1/namespaces/${NAMESPACE}/${resource}`;

const getConfigMapsAction = () => configMaps.createThunk(k8sRequest(base("configmaps")));
const getConfigMapAction = (name: string) =>
    configMap.createThunk(k8sRequest(`${base("configmaps")}/${encodeURIComponent(name)}`));
const getSecretsAction = () => secrets.createThunk(k8sRequest(base("secrets")));

const createConfigMapAction = (name: string, entries: { key: string; value: string }[]) =>
    created.createThunk({ ...k8sRequest(base("configmaps")), method: "POST", data: buildConfigMap(name, entries) });

const createSecretAction = (name: string, entries: { key: string; value: string }[]) =>
    created.createThunk({ ...k8sRequest(base("secrets")), method: "POST", data: buildSecret(name, entries) });

const resetConfigCreateAction = () => created.createResetAction();

export {
    getConfigMapsAction,
    getConfigMapAction,
    getSecretsAction,
    createConfigMapAction,
    createSecretAction,
    resetConfigCreateAction,
};
export const configMapsReducer = configMaps.reducer;
export const configMapReducer = configMap.reducer;
export const secretsReducer = secrets.reducer;
export const configCreateReducer = created.reducer;
